/**
 * Game detail V2 — the left rail (`design/Games Tonight.dc.html` lines 572-604).
 *
 * 292px outer, 26px of padding a side, so the cover measures exactly 240 — the
 * changelog's "Left rail (240px) — the game" and W.md's 292 are the same number
 * described from opposite sides of the padding. The rail ships 11px wider than
 * that, because the pane's own scrollbar has to come from somewhere and 292
 * leaves the 240 measure no slack to give it; see `GUTTER`.
 *
 * THE ART IS NEVER COVERED BY UI. The ribbons stay, because they are part of the
 * art's own state rather than chrome laid over it; nothing else paints on top.
 * Under it a bordered ledger states five facts, then the CTA stack.
 *
 * NO LIST CTA HERE. `Game Detail.dc.html` puts `✓ ON THE LIST — REMOVE` on the
 * rail and the two mirrors put `✕ REMOVE FROM TONIGHT'S LIST` in the FRIENDS
 * panel footer instead; W.md §6 arbitrates that disagreement for the mirrors,
 * which are the later pass. The V2 changelog entry sides with the frame — this
 * is that entry being overruled by the files it was written to describe, and it
 * is recorded here so a future reader comparing this screen against the
 * archived design/ mirrors does not read the absence as a miss.
 *
 * THE LEDGER STATES OR OMITS, NEVER APPROXIMATES. `ALL-TIME PEAK` renders only
 * when a curated peak exists — `hasPeak` gates the row, so an absent peak omits
 * it rather than printing an em dash under a label the dash would be answering.
 *
 * `peakTxt` is safe to bind here only because `detailVM` no longer overrides it.
 * It used to compose the peak with a live count (`— · 12K PLAYING NOW`), which
 * was correct under the old screen's `PEAK ` caption and a false claim under
 * this one. The live half is `nowTxt`, in the chart header. Re-introducing that
 * override is the single edit that would put a lie back on this row.
 *
 * `PLAY TOGETHER` OPENS THE EDITOR (W5), as both mirrors and the changelog
 * specify. It is inert on a row with no appid: the RPC's only possible answer
 * there is `UNKNOWN_GAME`, so the D-02 gate binds in this cell too and the tip
 * says why rather than letting the press fail. The tip otherwise NAMES THE
 * SOURCE — "why does this say 4?" is answered before the editor opens.
 *
 * `#0B0B12` → `var(--accSoft)`. The desktop mirror hand-edits that one cell to a
 * raw hex (note the spaces after its colons, unlike every sibling); the phone
 * mirror and the spec frame both use the token. W.md §6 calls it an intake
 * hazard; ported as the token.
 */

import { CoverArt } from '../../components/CoverArt.tsx';
import { Ribbon } from '../../components/Ribbon.tsx';
import { useQd } from '../../store/index.ts';
import { Bell } from '../../components/Bell.tsx';
import { DoorIcon } from '../../components/DoorIcon.tsx';
import { NO_STEAM_TIP } from '../../store/selectors.3c.ts';
import type { useArt } from '../../components/useArt.ts';
import type { DetailVM } from '../../store/selectors.ts';
import type { CSSProperties } from 'react';

const MONO = 'var(--gt-font-mono)';

/**
 * The 11px the desktop scrollbar occupies (`desktop.css` §1.2), added to the
 * rail's width rather than taken out of the cover's.
 *
 * 292 − 26 − 26 is exactly 240, so this rail has never had a single pixel of
 * slack, and TWO SEPARATE THINGS were competing for the same 240:
 *
 *   · the cover's own border. There is no global box-sizing reset in this app
 *     (`styles/global.css` has none), so `width:240` is a CONTENT box and the
 *     1px border a side made it measure 242. That is 2px of horizontal scroll
 *     on every rail, always, with nothing else involved — the visible symptom,
 *     since `overflow-y:auto` computes `overflow-x` to `auto` alongside it and
 *     an 11px bar appears under the pane to scroll two pixels. Fixed on the
 *     cover itself with `border-box`, not here.
 *   · the pane's scrollbar, which is what this constant is for. The rail is
 *     tall — a 360px cover above the ledger — so it overflows and claims its
 *     11px on any ordinary viewport, and 240 has nowhere to give them from.
 *
 * The width is spent in three places that must agree, or the cover, the ledger
 * and the CTA stack stop sharing a right edge: reserved in the scroller
 * (`stable`, so the measure does not jump as the pane crosses its overflow
 * threshold), mirrored on the CTA stack below it, and capped rather than fixed
 * on the cover so a platform whose bar is fatter than 11 shrinks the art
 * instead of bringing the sideways scroll back.
 */
const GUTTER = 11;

/** Every ledger row but the last, which has nothing under it to rule off. */
const ROW: CSSProperties = {
  display: 'flex',
  alignItems: 'baseline',
  gap: 9,
  padding: '10px 13px',
  borderBottom: '1px solid var(--gt-line)',
};

/** The row labels — mono 8.5 with the wide tracking the whole ledger shares. */
const LABEL: CSSProperties = {
  flex: 1,
  fontFamily: MONO,
  fontSize: 8.5,
  letterSpacing: '.11em',
  color: 'var(--gt-muted)',
};

export function DetailRail({
  d,
  art,
}: {
  d: DetailVM;
  art: ReturnType<typeof useArt>;
}): JSX.Element {
  const s = useQd();
  return (
    <div
      style={{
        width: 292 + GUTTER,
        flex: 'none',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        padding: '20px 26px 22px',
        boxSizing: 'border-box',
      }}
    >
      {/* Its own scroller: the page no longer scrolls, so a rail taller than the
          viewport has to carry its own overflow or its CTA stack is unreachable. */}
      <div
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          scrollbarGutter: 'stable',
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
        }}
      >
        <div
          style={{
            position: 'relative',
            // `border-box` because this app has no global box-sizing reset
            // (`styles/global.css` sets none): the mirror's bare `width:240`
            // is a 240 CONTENT box, and the 1px border a side made the cover
            // measure 242 inside a 240 column — 2px of sideways scroll on
            // every rail, scrollbar or no scrollbar. `maxWidth` rather than
            // `width` so the reserved GUTTER, not the art, absorbs a platform
            // scrollbar wider than 11.
            width: '100%',
            maxWidth: 240,
            boxSizing: 'border-box',
            aspectRatio: '2/3',
            flex: 'none',
            background: d.fall,
            overflow: 'hidden',
            border: '1px solid var(--gt-border)',
            boxShadow: '7px 7px 0 rgba(0,0,0,.45)',
          }}
        >
          <CoverArt style={{ position: 'absolute', inset: 0 }} fs={18} ini={d.ini} hasCover={art.hasCover} cover={art.cover} />
          <Ribbon up={d.upcoming} ea={d.ea} demo={d.demo} />
        </div>

        <div
          style={{
            border: '1px solid var(--gt-border)',
            background: 'var(--gt-panel)',
            flex: 'none',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* PLAY TOGETHER — the count, the crew read against it, and the way in
              to correcting it. Inert on a row with no appid: the RPC's only
              possible answer there is UNKNOWN_GAME, and a control whose sole
              outcome is an error should not look pressable. */}
          <div
            onClick={d.capAppid ? () => s.openCap(d.capAppid) : undefined}
            data-tip={d.capAppid ? d.capTip : NO_STEAM_TIP}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '11px 13px',
              borderBottom: '1px solid var(--gt-line)',
              background: 'var(--accSoft)',
              cursor: d.capAppid ? 'pointer' : 'default',
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: 1, minWidth: 0 }}>
              <span style={{ fontFamily: MONO, fontSize: 8.5, letterSpacing: '.11em', color: 'var(--gt-text-5)' }}>
                PLAY TOGETHER
              </span>
              <span
                style={{
                  fontFamily: 'var(--gt-font-pixel)',
                  fontSize: 17,
                  color: 'var(--acc)',
                  transform: 'translateY(0.13em)',
                }}
              >
                {d.capNum}
              </span>
              {/* RECONSTRUCTED. The export dropped this span — line 584 of the
                  desktop mirror is a blank line between the count and the close
                  of its column — while the same file's view-model still computes
                  `fitTxt` at :3158 and the phone mirror still renders it. W.md §6
                  says to rebuild from the view-model rather than read absence as
                  deletion. Without it the cell is a bare number and the seat
                  meter has nothing explaining what it counts. */}
              <span style={{ fontFamily: MONO, fontSize: 8, letterSpacing: '.05em', color: 'var(--gt-text-5)' }}>
                {d.fitTxt}
              </span>
            </div>
            {/* One cell per crew member. Two columns, so six people read as the
                prototype's 2×3 and a larger crew grows rows rather than
                overflowing — see `seatCells` for why the count is not fixed. */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3, flex: 'none' }}>
              {d.seatCells.map((c) => (
                <span
                  key={c.key}
                  style={{ width: 11, height: 11, boxSizing: 'border-box', background: c.bg, border: c.bd }}
                />
              ))}
            </div>
          </div>

          <div style={ROW}>
            <span style={LABEL}>RELEASED</span>
            <span style={{ fontFamily: MONO, fontSize: 11.5, color: 'var(--gt-text)' }}>{d.dateFull}</span>
          </div>

          <div style={ROW}>
            <span style={LABEL}>PRICE</span>
            {d.hasOld ? (
              <span style={{ fontFamily: MONO, fontSize: 10, textDecoration: 'line-through', color: 'var(--gt-muted-2)' }}>
                {d.oldTxt}
              </span>
            ) : null}
            <span style={{ fontFamily: MONO, fontSize: 14, color: 'var(--gt-text)' }}>{d.priceTxt}</span>
            {d.hasDisc ? (
              <span
                style={{
                  fontFamily: MONO,
                  fontWeight: 700,
                  fontSize: 10,
                  background: 'var(--acc)',
                  color: 'var(--gt-ink)',
                  padding: '3px 5px',
                }}
              >
                {d.disc}
              </span>
            ) : null}
          </div>

          {/* The ALL-TIME PEAK row is gone (DESIGN-CHANGELOG 2026-09-03 "Detail
              cleanup"): the number lives on the chart and the tiles. */}

          {/* Last row, so no rule under it. One mono line, not chips — V2 cuts
              the chip row and this is where the tags land instead. */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 7, padding: '10px 13px' }}>
            <span style={LABEL}>TAGS</span>
            <span
              style={{
                fontFamily: MONO,
                fontSize: 9,
                lineHeight: 1.7,
                letterSpacing: '.06em',
                color: 'var(--gt-text-5)',
              }}
            >
              {d.tagsTxt}
            </span>
          </div>
        </div>
      </div>

      {/* Outside the scroller: the CTA stack is pinned to the bottom of the rail
          rather than scrolling away with the ledger. It pays the same GUTTER by
          hand, since it is not the thing the scrollbar is reserved inside. */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flex: 'none', marginRight: GUTTER }}>
        <a
          href={d.buyUrl}
          target="_blank"
          rel="noreferrer"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '1px solid var(--gt-border-2)',
            color: 'var(--gt-text-3)',
            fontFamily: MONO,
            fontSize: 10.5,
            padding: '11px 12px',
            textDecoration: 'none',
          }}
        >
          {d.buyTxt} ↗
        </a>
        {d.hasDemoBtn ? (
          <a
            href={d.demoUrl}
            target="_blank"
            rel="noreferrer"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px solid var(--acc2)',
              color: 'var(--acc2)',
              fontFamily: MONO,
              fontSize: 10.5,
              padding: '11px 12px',
              textDecoration: 'none',
            }}
          >
            PLAY THE FREE DEMO ↗
          </a>
        ) : null}
        {/* The score control (DESIGN-CHANGELOG 2026-09-12 "Scoring is not a
            backlog privilege"; `design/GT Discover.dc.html:184`): the top of
            the action stack, above RECOMMEND ▸. `SCORE THIS GAME` + `—`, or
            `MY SCORE` + the number, the box's edge in the score's own colour
            once set. Opens the one YOUR SCORE modal — no new modal, no new
            write path — for ANY game, shelved or not. */}
        <div
          onClick={() => s.openScore(d.id)}
          data-tip={d.scoreTip}
          className="d-hov-acc-box"
          style={{
            display: 'flex',
            gap: 9,
            alignItems: 'center',
            height: 40,
            padding: '0 12px',
            border: '1px solid ' + d.scoreBd,
            fontFamily: MONO,
            fontSize: 10.5,
            letterSpacing: '.05em',
            color: 'var(--gt-text-3)',
            cursor: 'pointer',
            boxSizing: 'border-box',
          }}
        >
          <DoorIcon kind="bars" size={13} />
          <span style={{ flex: 1 }}>{d.scoreTxt}</span>
          <span style={{ fontFamily: 'var(--gt-font-pixel)', fontSize: 13, color: d.scoreClr, display: 'flex', alignItems: 'center', flex: 'none' }}>
            <span style={{ transform: 'translateY(0.13em)' }}>{d.scoreNum}</span>
          </span>
        </div>
        {/* RECOMMEND ▸ (AA phase 3; `design/GT Discover.dc.html:182`): under
            STEAM / DEMO, above the backlog row. A personal action — you are
            the one talking. With nobody else in the party it stays, inert,
            and the tip says so. */}
        <div
          onClick={() => (d.sendOn ? s.sdOpen(d.id) : undefined)}
          data-tip={d.sendTip}
          className={d.sendOn ? 'd-hov-acc-box' : undefined}
          style={{
            display: 'flex',
            gap: 9,
            alignItems: 'center',
            justifyContent: 'center',
            height: 40,
            border: '1px solid var(--gt-border-2)',
            fontFamily: MONO,
            fontSize: 9.5,
            letterSpacing: '.06em',
            color: d.sendOn ? 'var(--gt-text-2)' : 'var(--gt-text-5)',
            cursor: d.sendOn ? 'pointer' : 'default',
            boxSizing: 'border-box',
          }}
        >
          <DoorIcon kind="hand" size={13} />
          <span>RECOMMEND ▸</span>
        </div>
        {/* `+ ADD TO MY BACKLOG` / `✓ ON YOUR BACKLOG` (DESIGN-CHANGELOG
            2026-09-03 "Detail cleanup"; `design/GT Discover.dc.html:184`). A
            personal action, so it lives in THIS rail with VIEW ON STEAM and not
            in the party panel. The diamond is the BACKLOG door's own glyph —
            an outline when it is not shelved, filled-centre when it is. */}
        <div
          onClick={() => (d.soloOn ? void s.soloRm(d.id) : void s.soloAdd(d.id))}
          data-tip={d.soloTip}
          className="d-hov-acc-box"
          style={{
            display: 'flex',
            gap: 9,
            alignItems: 'center',
            justifyContent: 'center',
            height: 40,
            border: '1px solid var(--gt-border-2)',
            fontFamily: MONO,
            fontSize: 9.5,
            letterSpacing: '.06em',
            color: d.soloClr,
            cursor: 'pointer',
            boxSizing: 'border-box',
          }}
        >
          <svg
            viewBox="0 0 9 9"
            style={{ width: 13, height: 13, display: 'block', flex: 'none', color: d.soloOn ? 'var(--gt-text)' : 'var(--gt-text-5)' }}
            shapeRendering="crispEdges"
            aria-hidden="true"
          >
            <path d="M4 0h1v1H4zM3 1h1v1H3zM5 1h1v1H5zM2 2h1v1H2zM6 2h1v1H6zM1 3h1v1H1zM7 3h1v1H7zM0 4h1v1H0zM8 4h1v1H8zM1 5h1v1H1zM7 5h1v1H7zM2 6h1v1H2zM6 6h1v1H6zM3 7h1v1H3zM5 7h1v1H5zM4 8h1v1H4z" fill="currentColor" />
            {d.soloOn ? <path d="M4 3h1v1H4zM3 4h3v1H3zM4 5h1v1H4z" fill="var(--acc)" /> : null}
          </svg>
          <span>{d.soloTxt}</span>
        </div>
        {/* The bell (AA phase 4; `design/GT Discover.dc.html:185-189`): solid
            + count when something is armed, hollow when nothing is — the same
            footprint either way. Opens TELL ME WHEN. */}
        <div
          onClick={() => s.wOpen(d.id)}
          data-tip={d.bellTip}
          className="d-hov-acc-box"
          style={{
            display: 'flex',
            gap: 7,
            alignItems: 'center',
            justifyContent: 'center',
            height: 40,
            border: '1px solid ' + d.bellBd,
            fontFamily: MONO,
            fontSize: 9.5,
            letterSpacing: '.06em',
            color: d.bellClr,
            cursor: 'pointer',
            boxSizing: 'border-box',
          }}
        >
          <Bell on={d.bellOn} size={13} color={d.bellClr} />
          <span>{d.bellOn ? 'WATCHING ' + d.bellN : 'TELL ME WHEN'}</span>
        </div>
      </div>
    </div>
  );
}
